import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Button,
  Chip,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  EventNote,
  AccountBalanceWallet,
  MonetizationOn,
  LocalFireDepartment,
  LocalLibrary,
  Psychology,
  CardMembership,
  School,
  LocationOn,
  CalendarToday,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { AuthContext } from '../../context/AuthContext';
import { useCityContext } from '../../context/CityContext';
import api from '../../utils/api';
import BannerCarousel from '../../components/BannerCarousel';
import LibraryCard from '../../components/LibraryCard';
import GradientSurface from '../../components/shared/GradientSurface';
import StatCard from '../../components/shared/StatCard';
import SectionHeader from '../../components/shared/SectionHeader';
import EmptyState from '../../components/shared/EmptyState';
import LoadingSkeleton from '../../components/shared/LoadingSkeleton';
import BMLCoinBalance from '../../components/coins/BMLCoinBalance';
import SurfaceCard from '../../components/shared/SurfaceCard';
import PageShell from '../../components/shared/PageShell';
import { listContainerVariants, listItemVariants } from '../../theme/motion';
import { RADIUS } from '../../theme/tokens';
import { useReducedMotionPreferred } from '../../hooks/useReducedMotionPreferred';

const MotionBox = motion(Box);

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

/**
 * StudentHome — dashboard page rendered inside the StudentPortal shell.
 * ActiveSessionBanner is NOT rendered here — the shell owns it.
 */
function StudentHome({ showSnackbar }) {
  const { user } = useContext(AuthContext);
  const { effectiveCity } = useCityContext();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const reducedMotion = useReducedMotionPreferred();

  const [stats, setStats] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);
  const [coinBalance, setCoinBalance] = useState(null);
  const [currentStreak, setCurrentStreak] = useState(null);
  const [upcomingBooking, setUpcomingBooking] = useState(null);
  const [trendingLibraries, setTrendingLibraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  // Stagger only on first mount — not on every tab return
  const hasStaggered = useRef(false);
  const [shouldAnimate, setShouldAnimate] = useState(false);

  useEffect(() => {
    if (!hasStaggered.current) {
      hasStaggered.current = true;
      setShouldAnimate(true);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [bookingsRes, librariesRes, walletRes, coinsRes] = await Promise.all([
        api.get('/bookings'),
        api.get('/libraries'),
        api.get('/wallet/balance').catch(() => ({ data: { data: { wallet: null } } })),
        api.get('/coins/balance').catch(() => ({ data: { data: { balance: null, current_streak: null } } })),
      ]);

      const bookings = bookingsRes.data.data.bookings || [];
      const active = bookings.filter(b => b.status === 'active' || b.status === 'upcoming');

      setStats({ activeBookings: active.length });
      setUpcomingBooking(active[0] ?? null);
      setTrendingLibraries((librariesRes.data.data.libraries || []).slice(0, 3));
      setWalletBalance(walletRes.data.data.wallet?.balance_display ?? null);
      setCoinBalance(coinsRes.data.data.balance ?? null);
      setCurrentStreak(coinsRes.data.data.current_streak ?? null);
      setFetchError(false);
    } catch {
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  };

  const containerAnim = shouldAnimate && !reducedMotion
    ? listContainerVariants
    : { animate: {} };

  const itemAnim = shouldAnimate && !reducedMotion
    ? listItemVariants
    : { initial: { opacity: 1, y: 0 }, animate: { opacity: 1, y: 0 } };

  const quickActions = [
    { label: 'Libraries', icon: <LocalLibrary />, path: '/student/libraries' },
    { label: 'Focus Mode', icon: <Psychology />, path: '/student/focus-mode' },
    { label: 'Subscriptions', icon: <CardMembership />, path: '/student/subscriptions' },
    { label: 'Institutes', icon: <School />, path: '/student/institutes' },
  ];

  const firstName = user?.name?.split(' ')[0] ?? 'Student';

  if (loading) {
    return (
      <PageShell
        title={`${getGreeting()}, ${firstName}`}
        maxWidth="sm"
      >
        <LoadingSkeleton variant="hero" />
        <Grid container spacing={2} mb={3}>
          {[0, 1, 2, 3].map(i => (
            <Grid item xs={6} key={i}>
              <LoadingSkeleton variant="stat" />
            </Grid>
          ))}
        </Grid>
        <LoadingSkeleton variant="list" count={3} />
      </PageShell>
    );
  }

  const leftColumn = (
    <>
      {/* Stat grid 2x2 */}
      <MotionBox variants={itemAnim} mb={2.5}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <StatCard
              icon={<EventNote />}
              label="Active bookings"
              value={fetchError ? '—' : (stats?.activeBookings ?? 0)}
              tone="primary"
              onClick={() => navigate('/student/access', { state: { tab: 'bookings', bookingsFilter: 'active' } })}
            />
          </Grid>
          <Grid item xs={6}>
            <StatCard
              icon={<AccountBalanceWallet />}
              label="Wallet"
              value={fetchError ? '—' : (walletBalance ?? '—')}
              tone="accent"
              onClick={() => navigate('/student/wallet')}
            />
          </Grid>
          <Grid item xs={6}>
            <StatCard
              icon={<MonetizationOn />}
              label="BML Coins"
              value={fetchError ? '—' : (coinBalance ?? 0)}
              tone="coin"
              helper={currentStreak ? `${currentStreak}d streak` : undefined}
              onClick={() => navigate('/student/coins')}
            />
          </Grid>
          <Grid item xs={6}>
            <StatCard
              icon={<LocalFireDepartment />}
              label="Study streak"
              value={fetchError ? '—' : (currentStreak ?? 0)}
              tone="success"
              onClick={() => navigate('/student/statistics')}
            />
          </Grid>
        </Grid>
      </MotionBox>

      {/* Quick actions */}
      <MotionBox variants={itemAnim} mb={2.5}>
        <Grid container spacing={1.5}>
          {quickActions.map(action => (
            <Grid item xs={6} key={action.path}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={action.icon}
                onClick={() => navigate(action.path)}
                sx={{
                  fontWeight: 600,
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                  px: 2,
                }}
              >
                {action.label}
              </Button>
            </Grid>
          ))}
        </Grid>
      </MotionBox>

      {/* Next up */}
      <MotionBox variants={itemAnim} mb={2.5}>
        <SectionHeader
          title="Next up"
          icon={<EventNote />}
          actionLabel="All bookings"
          onAction={() => navigate('/student/access', { state: { tab: 'bookings' } })}
        />
        {upcomingBooking ? (
          <SurfaceCard
            interactive
            radius="lg"
            elevation="sm"
            padding={2}
            onClick={() => navigate('/student/access', { state: { tab: 'bookings' } })}
          >
            <Box display="flex" gap={2} alignItems="flex-start">
              <GradientSurface
                variant="brand"
                radius={RADIUS.md}
                sx={{
                  width: 48,
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '1.25rem',
                }}
              >
                {upcomingBooking.library?.name?.charAt(0) ?? 'L'}
              </GradientSurface>
              <Box flex={1} minWidth={0}>
                <Typography variant="subtitle2" fontWeight={700} noWrap>
                  {upcomingBooking.library?.name ?? 'Library'}
                </Typography>
                {upcomingBooking.library?.address && (
                  <Box display="flex" alignItems="center" gap={0.5} mt={0.25}>
                    <LocationOn sx={{ fontSize: 13, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {upcomingBooking.library.address.substring(0, 40)}
                    </Typography>
                  </Box>
                )}
                {upcomingBooking.created_at && (
                  <Box display="flex" alignItems="center" gap={0.5} mt={0.25}>
                    <CalendarToday sx={{ fontSize: 13, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      {new Date(upcomingBooking.created_at).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Chip
                label={upcomingBooking.status}
                size="small"
                color={upcomingBooking.status === 'active' ? 'success' : 'primary'}
                sx={{ fontWeight: 600, textTransform: 'capitalize', flexShrink: 0 }}
              />
            </Box>
          </SurfaceCard>
        ) : (
          <EmptyState
            icon={<EventNote />}
            title="No upcoming bookings"
            description="Book a library seat to get started"
            actionLabel="Browse libraries"
            onAction={() => navigate('/student/libraries')}
            sx={{ py: 3 }}
          />
        )}
      </MotionBox>

    </>
  );

  const rightColumn = (
    <>
      {/* Nearby libraries */}
      <MotionBox variants={itemAnim} mb={2.5}>
        <SectionHeader
          title="Libraries near you"
          icon={<LocalLibrary />}
          actionLabel="See all"
          onAction={() => navigate('/student/libraries')}
        />
        {trendingLibraries.length === 0 ? (
          <EmptyState
            icon={<LocalLibrary />}
            title="No libraries found"
            actionLabel="Browse libraries"
            onAction={() => navigate('/student/libraries')}
            sx={{ py: 3 }}
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              overflowX: isDesktop ? 'visible' : 'auto',
              flexDirection: isDesktop ? 'column' : 'row',
              pb: isDesktop ? 0 : 1,
              // hide scrollbar visually but keep scroll functional
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            }}
          >
            {trendingLibraries.map(lib => (
              <Box key={lib.id} sx={{ flexShrink: 0, width: isDesktop ? '100%' : 260 }}>
                <LibraryCard library={lib} />
              </Box>
            ))}
          </Box>
        )}
      </MotionBox>

      {/* Coins teaser */}
      <MotionBox variants={itemAnim} mb={2.5}>
        <SectionHeader
          title="Your BML Coins"
          icon={<MonetizationOn />}
          actionLabel="View all"
          onAction={() => navigate('/student/coins')}
        />
        <BMLCoinBalance balance={coinBalance ?? 0} variant="large" showTooltip={false} />
      </MotionBox>

      {/* Banner */}
      <MotionBox variants={itemAnim}>
        <BannerCarousel location="dashboard" userCity={effectiveCity} />
      </MotionBox>
    </>
  );

  return (
    <PageShell
      title={`${getGreeting()}, ${firstName}`}
      maxWidth={isDesktop ? 'md' : 'sm'}
    >
      <MotionBox
        initial={shouldAnimate && !reducedMotion ? 'initial' : false}
        animate="animate"
        variants={containerAnim}
      >
        {isDesktop ? (
          <Grid container spacing={3}>
            <Grid item md={7}>{leftColumn}</Grid>
            <Grid item md={5}>{rightColumn}</Grid>
          </Grid>
        ) : (
          <>
            {leftColumn}
            {rightColumn}
          </>
        )}
      </MotionBox>
    </PageShell>
  );
}

export default StudentHome;
